import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { deleteBoard } from '../../store/boardSlice';
import { fetchWorkspaceBoards } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { Board } from '../../types/common';

interface DeleteBoardModalProps {
  open: boolean;
  onClose: () => void;
  board: Board | null;
  workspaceId: string;
}

const DeleteBoardModal: React.FC<DeleteBoardModalProps> = ({ open, onClose, board, workspaceId }) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.boards);

  const handleDelete = async () => {
    if (!board) return;

    try {
      await dispatch(deleteBoard(board.id)).unwrap();

      // Refresh workspace boards
      await dispatch(fetchWorkspaceBoards(workspaceId));

      dispatch(
        showToast({
          message: 'Board deleted successfully!',
          type: 'success',
        })
      );

      onClose();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error.message || 'Failed to delete board',
          type: 'error',
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={24} color="#e44258" />
          <span>Delete Board</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete <strong>{board?.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This action cannot be undone. All tasks, sprints, and data associated with this board will be permanently deleted.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Delete Board'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteBoardModal;
